import { Box, Stack } from '@mui/material';
import { Fragment, type ComponentProps, type ReactNode, type Ref } from 'react';

import type { WallCard } from '@walls/store';
import { ClusterPlacementPlaceholder } from './ClusterPlacementPlaceholder';
import type { ClusterChildSortDrag, ClusterPlacementSize } from './types';

type CardsContainerPointerHandlers = Pick<
  ComponentProps<typeof Stack>,
  'onLostPointerCapture' | 'onPointerCancel' | 'onPointerMove' | 'onPointerUp'
>;

interface ClusterChildListProps {
  activeClusterPlacementPreview: {
    placeholderBeforeChildId: string | null;
    size: ClusterPlacementSize;
  } | null;
  canSortChild: (childCard: WallCard) => boolean;
  cardsContainerPointerHandlers: CardsContainerPointerHandlers;
  cardsContainerRef: Ref<HTMLDivElement>;
  childCount: number;
  childSortDrag: ClusterChildSortDrag | null;
  cluster: WallCard;
  getChildSortHandlers: (childCard: WallCard) => ComponentProps<typeof Box>;
  isResizedCluster: boolean;
  orderedChildren: WallCard[];
  renderChild: (childCard: WallCard) => ReactNode;
  showSourcePlacementPreview: boolean;
}

export function ClusterChildList({
  activeClusterPlacementPreview,
  canSortChild,
  cardsContainerPointerHandlers,
  cardsContainerRef,
  childCount,
  childSortDrag,
  cluster,
  getChildSortHandlers,
  isResizedCluster,
  orderedChildren,
  renderChild,
  showSourcePlacementPreview,
}: ClusterChildListProps) {
  return (
    <Stack
      ref={cardsContainerRef}
      id={`cards-container_${cluster.id}`}
      {...cardsContainerPointerHandlers}
      sx={{
        alignContent: isResizedCluster ? 'flex-start' : undefined,
        alignItems: 'flex-start',
        flexDirection: isResizedCluster ? 'row' : 'column',
        flexWrap: isResizedCluster ? 'wrap' : undefined,
        boxSizing: 'border-box',
        flex: isResizedCluster ? '1 1 auto' : undefined,
        minHeight: isResizedCluster && childCount > 0 ? 0 : childCount === 0 ? 48 : undefined,
        overflow: isResizedCluster ? 'auto' : undefined,
        p: '10px',
        gap: 1,
        width: isResizedCluster ? cluster.width : undefined,
      }}
    >
      {orderedChildren.length === 0 && !childSortDrag && !activeClusterPlacementPreview && (
        <Box
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.5)',
            color: 'text.secondary',
            fontSize: 13,
            p: 1,
            width: '100%',
          }}
        >
          &nbsp;
        </Box>
      )}
      {orderedChildren.map((childCard) => (
        <Fragment key={childCard.id}>
          {showSourcePlacementPreview &&
            childSortDrag?.placeholderBeforeChildId === childCard.id && (
              <ClusterPlacementPlaceholder size={childSortDrag.boardSize} />
            )}
          {activeClusterPlacementPreview?.placeholderBeforeChildId === childCard.id && (
            <ClusterPlacementPlaceholder size={activeClusterPlacementPreview.size} />
          )}
          {childSortDrag?.childId !== childCard.id && (
            <Box
              data-cluster-child-id={childCard.id}
              sx={{
                cursor: canSortChild(childCard) ? 'move' : 'default',
                touchAction: canSortChild(childCard) ? 'none' : undefined,
                width: isResizedCluster ? undefined : '100%',
                '& > [data-element-type]': {
                  minWidth: isResizedCluster ? undefined : '100%',
                },
              }}
              {...getChildSortHandlers(childCard)}
            >
              {renderChild(childCard)}
            </Box>
          )}
        </Fragment>
      ))}
      {showSourcePlacementPreview &&
        childSortDrag &&
        childSortDrag.placeholderBeforeChildId == null && (
          <ClusterPlacementPlaceholder size={childSortDrag.boardSize} />
        )}
      {activeClusterPlacementPreview &&
        activeClusterPlacementPreview.placeholderBeforeChildId == null && (
          <ClusterPlacementPlaceholder size={activeClusterPlacementPreview.size} />
        )}
    </Stack>
  );
}
